import {
  ArrowDownToLineIcon,
  ArrowUpToLineIcon,
  CopyIcon,
  EllipsisVerticalIcon,
  PencilIcon,
  PowerIcon,
  PowerOffIcon,
  Trash2Icon,
} from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { MODAL_DELETE } from '@/components/organisms/Modals/ModalConductor';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDeskId } from '@/hooks/useDeskId';
import { RENAME_MODE, RULE_FORM_ID } from '@/modules/rules/constants';
import { useDuplicateRule } from '@/modules/rules/hooks/useDuplicateRule';
import { ReorderProps, useRuleMutations } from '@/modules/rules/hooks/useRules';
import { removeFormState } from '@/redux/forms/actions';
import { popModal, pushModal } from '@/redux/modals/actions';
import { Rule } from '@/shared/types/rules';

interface ActionsProps {
  rule: Rule;
  rulesPath: string;
  isSelected: boolean;
  currentPosition: number;
  totalRules: number;
  onReorder: (params: ReorderProps) => void;
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const Actions = ({
  rule,
  rulesPath,
  isSelected,
  currentPosition,
  totalRules,
  onReorder,
  disabled,
  onOpenChange,
}: ActionsProps) => {
  const { rule_id, name, status } = rule;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const deskId = useDeskId();
  const { updateRule, deleteRule } = useRuleMutations(deskId);
  const { duplicateRule, isDuplicating } = useDuplicateRule();
  const navigate = useNavigate();

  const isActive = status === 'active';
  const isFirst = currentPosition === 1;
  const isLast = currentPosition === totalRules;

  const handleDeactivate = () => {
    updateRule({ ...rule, status: 'inactive' });
  };

  const handleActivate = () => {
    updateRule({ ...rule, status: 'active' });
  };

  const handleDelete = () => {
    const deleteProps = {
      subtitle: <Trans i18nKey="rules.delete_warning" values={[name]} />,
      confirm: true,
      onDelete: () => {
        deleteRule(rule_id, {
          onSuccess: () => {
            dispatch(popModal());
            if (isSelected) {
              dispatch(removeFormState({ formId: RULE_FORM_ID }));
              navigate(rulesPath);
            }
          },
        });
      },
    };
    dispatch(pushModal(MODAL_DELETE, deleteProps));
  };

  const handleSendToTop = () => {
    onReorder({ rule_id, position: 1 });
  };

  const handleSendToBottom = () => {
    onReorder({ rule_id, position: totalRules });
  };

  return (
    <DropdownMenu modal={false} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          aria-label={t('rules.actions_menu', { 0: name })}
          variant="ghost-neutral"
          size="icon-sm"
          disabled={disabled}
        >
          <EllipsisVerticalIcon aria-hidden="true" size={16} />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {isActive ? (
          <DropdownMenuItem onSelect={handleDeactivate} className="flex items-center gap-2">
            <PowerOffIcon size={16} className="text-foreground-muted" />
            {t('rules.deactivate')}
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onSelect={handleActivate} className="flex items-center gap-2">
            <PowerIcon size={16} className="text-foreground-muted" />
            {t('rules.activate')}
          </DropdownMenuItem>
        )}

        <DropdownMenuItem
          onSelect={() => navigate(`${rulesPath}/${rule_id}`)}
          className="flex items-center gap-2"
        >
          <PencilIcon size={16} className="text-foreground-muted" />
          {t('common.edit')}…
        </DropdownMenuItem>

        <DropdownMenuItem
          onSelect={() => navigate(`${rulesPath}/${rule_id}?mode=${RENAME_MODE}`)}
          className="flex items-center gap-2"
        >
          <span className="size-4" aria-hidden />
          {t('common.rename')}…
        </DropdownMenuItem>

        <DropdownMenuItem
          onSelect={() => duplicateRule(rule)}
          disabled={isDuplicating}
          className="flex items-center gap-2"
        >
          <CopyIcon size={16} className="text-foreground-muted" />
          {t('rules.duplicate')}
        </DropdownMenuItem>

        <DropdownMenuItem
          variant="destructive"
          onSelect={handleDelete}
          className="flex items-center gap-2"
        >
          <Trash2Icon size={16} />
          {t('common.delete')}…
        </DropdownMenuItem>

        {(!isFirst || !isLast) && <DropdownMenuSeparator />}

        {!isFirst && (
          <DropdownMenuItem onSelect={handleSendToTop} className="flex items-center gap-2">
            <ArrowUpToLineIcon size={16} className="text-foreground-muted" />
            {t('rules.send_to_top')}
          </DropdownMenuItem>
        )}

        {!isLast && (
          <DropdownMenuItem onSelect={handleSendToBottom} className="flex items-center gap-2">
            <ArrowDownToLineIcon size={16} className="text-foreground-muted" />
            {t('rules.send_to_bottom')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
